import React from 'react';
import Icon from '../../../components/AppIcon';

const PlatformBenefits = () => {
  const benefits = [
    {
      icon: 'Target',
      title: 'Streamlined Quote Management',
      description: 'Organize, track, and manage all your quotes in one centralized dashboard with automated follow-ups and deadline reminders.',
      features: ['Quote tracking dashboard', 'Automated reminders', 'Status updates']
    },
    {
      icon: 'MessageCircle',
      title: 'Direct Client Communication',
      description: 'Built-in messaging system for seamless communication with clients, project updates, and real-time collaboration.',
      features: ['In-app messaging', 'File sharing', 'Project updates']
    },
    {
      icon: 'CreditCard',
      title: 'Secure Payment Processing',
      description: 'Get paid faster with our integrated payment system, milestone billing, and automatic invoice generation.',
      features: ['Secure payments', 'Milestone billing', 'Auto invoicing']
    },
    {
      icon: 'BarChart3',
      title: 'Business Analytics',
      description: 'Track your performance with detailed analytics on quote success rates, revenue trends, and client satisfaction.',
      features: ['Performance metrics', 'Revenue tracking', 'Success analytics']
    },
    {
      icon: 'Star',
      title: 'Review & Rating System',
      description: 'Build your reputation with client reviews and ratings that help you win more projects and premium opportunities.',
      features: ['Client reviews', 'Rating system', 'Reputation building']
    },
    {
      icon: 'Users',
      title: 'Network Growth',
      description: 'Connect with other vendors, share resources, and access referral opportunities within the Stackd ecosystem.',
      features: ['Vendor network', 'Referral system', 'Resource sharing']
    }
  ];

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Why Choose Stackd Vendor Base?
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Join thousands of successful vendors who have transformed their business with our comprehensive platform
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {benefits?.map((benefit, index) => (
            <div key={index} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name={benefit?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  {benefit?.title}
                </h3>
              </div>
              
              <p className="text-text-secondary mb-4 leading-relaxed">
                {benefit?.description}
              </p>

              <ul className="space-y-2">
                {benefit?.features?.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-text-secondary">
                    <Icon name="Check" size={16} className="text-success mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Success Stories */}
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl p-8 mb-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-foreground mb-2">
              Success Stories from Our Vendors
            </h3>
            <p className="text-text-secondary">
              Real results from vendors using Stackd Vendor Base
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="TrendingUp" size={32} className="text-success" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">152%</div>
              <p className="text-text-secondary">Average revenue increase in first 6 months</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Clock" size={32} className="text-primary" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">73%</div>
              <p className="text-text-secondary">Faster payment processing compared to traditional methods</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Star" size={32} className="text-warning" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">4.9★</div>
              <p className="text-text-secondary">Average vendor rating on our platform</p>
            </div>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Everything You Need to Succeed
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <Icon name="Zap" size={20} className="text-primary mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Instant Notifications</h4>
                  <p className="text-text-secondary">Get notified immediately when new quote opportunities match your services and location.</p>
                </div>
              </div>

              <div className="flex items-start">
                <Icon name="Shield" size={20} className="text-success mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Verified Clients</h4>
                  <p className="text-text-secondary">All clients are pre-screened and verified, ensuring legitimate projects and secure payments.</p>
                </div>
              </div>

              <div className="flex items-start">
                <Icon name="Smartphone" size={20} className="text-warning mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Mobile-First Design</h4>
                  <p className="text-text-secondary">Manage your business on the go with our fully responsive mobile platform and app.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 rounded-xl p-8">
            <div className="text-center">
              <h4 className="text-xl font-bold text-foreground mb-4">
                Ready to Get Started?
              </h4>
              <div className="space-y-3 text-left">
                <div className="flex items-center">
                  <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                  <span className="text-text-secondary">Complete your vendor profile</span>
                </div>
                <div className="flex items-center">
                  <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  <span className="text-text-secondary">Browse and respond to quote requests</span>
                </div>
                <div className="flex items-center">
                  <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                  <span className="text-text-secondary">Win projects and grow your business</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformBenefits;